// ============================================================================
// QuestionsTab — onglet QUESTIONS de l'ecran Admin (AD-F : atelier de sondage)
// ============================================================================
// Porte du PoC questionnaires-section SANS le sous-onglet Courriel (STOP-1 :
// mailer ACS differe AD-G — aucun EmailEditor/EmailPreview/reminderContent).
// Trois blocs d'edition (Modele / Surcouche / Banque) + apercu vivant
// (SurveyPreview) sur les copies de travail.
//
// P-ADM-14 : les selecteurs (sondage, equipe, periode) sont de l'ETAT REACT
// (leve dans AdminPage, re-cle la query) — JAMAIS le <form method=GET> du PoC
// qui rechargeait la page. Changer de contexte REINITIALISE les copies de
// travail (equivalent SPA du reload PoC — un brouillon member_pulse ne doit
// jamais s'afficher sous team_pulse).
//
// Questions coeur (coreKey != null) : rendues VERROUILLEES (badge Coeur —
// type/obligation/suppression masques, parite PoC locked) ; seuls libelle et
// bornes s'editent. La garde UI n'est PAS la protection : le serveur re-derive
// les champs coeur et applique assertCoreInvariant (P-ADM-4) — un payload forge
// revient en 400 core_invariant (banniere d'erreur).
//
// Copies de travail : la copie n'adopte l'etat serveur QUE si elle est propre
// (ou apres sa propre sauvegarde / un changement de contexte) — une
// invalidation declenchee par un autre bloc (ex. ajout banque) ne doit pas
// ecraser un brouillon en cours.

import { useMemo, useState } from 'react';
import {
  ChevronDown, ChevronUp, ClipboardList, Loader2, Lock, MessageSquarePlus, Plus,
  Save, Sparkles, Trash2,
} from 'lucide-react';
import { EmptyState, GsSegmented, GsSelect, QueryError, SkeletonCard, ConfirmDialog, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { currentMonth, formatPeriodLong } from '../../lib/pulse/period';
import {
  useAddBankQuestion, useRemoveBankQuestion, useSaveQuestionOverlay, useSaveQuestionTemplate,
  adminQuestionsErrorCode,
  PULSE_ANSWER_TYPES, PULSE_SURVEY_TYPES,
  type AdminBankQuestion, type AdminPulseQuestion, type AdminQuestionsResponse,
  type PulseAnswerType, type PulseQuestionPayload, type PulseSurveyType,
} from '../../hooks/useAdminQuestions';
import { SectionHeader } from './SectionHeader';
import { SurveyPreview } from './SurveyPreview';

// ─── Copie de travail (formulaire : '' au lieu de null) ──────────────────────

interface WorkingQuestion {
  id: string;
  coreKey: AdminPulseQuestion['coreKey'];
  questionType: PulseAnswerType;
  prompt: string;
  label: string;
  low: string;
  high: string;
  required: boolean;
}

function toWorking(q: AdminPulseQuestion): WorkingQuestion {
  return {
    id: q.id,
    coreKey: q.coreKey,
    questionType: q.questionType,
    prompt: q.prompt,
    label: q.label ?? '',
    low: q.low ?? '',
    high: q.high ?? '',
    required: q.required,
  };
}

function toPayload(q: WorkingQuestion): PulseQuestionPayload {
  return {
    id: q.id,
    questionType: q.questionType,
    prompt: q.prompt.trim(),
    label: q.label.trim(),
    low: q.low.trim(),
    high: q.high.trim(),
    required: q.required,
  };
}

function toPreview(qs: WorkingQuestion[]): AdminPulseQuestion[] {
  return qs.map((q, i) => ({
    id: q.id,
    coreKey: q.coreKey,
    questionType: q.questionType,
    prompt: q.prompt,
    label: q.label || null,
    low: q.low || null,
    high: q.high || null,
    required: q.required,
    sortOrder: i,
  }));
}

function bankToWorking(b: AdminBankQuestion): WorkingQuestion {
  return {
    id: crypto.randomUUID(),
    coreKey: null,
    questionType: b.questionType,
    prompt: b.prompt,
    label: b.label ?? '',
    low: b.low ?? '',
    high: b.high ?? '',
    required: false,
  };
}

/** Deplace l'element a index d'un cran (dir -1 = haut, +1 = bas). */
function reorder<T>(arr: T[], index: number, dir: -1 | 1): T[] {
  const target = index + dir;
  if (target < 0 || target >= arr.length) return arr;
  const next = arr.slice();
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

const labelCls = 'text-meta font-medium text-slate-600 dark:text-slate-300';
const iconBtnCls =
  'inline-flex h-8 w-8 touch-manipulation items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:pointer-events-none disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200';
const dangerBtnCls =
  'inline-flex h-8 w-8 touch-manipulation items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:pointer-events-none disabled:opacity-40 dark:text-slate-400 dark:hover:bg-red-950 dark:hover:text-red-400';

// ─── Composant principal ─────────────────────────────────────────────────────

export function QuestionsTab({
  isLoading,
  isError,
  data,
  onRetry,
  surveyType,
  onSurveyTypeChange,
  teamId,
  onTeamIdChange,
  period,
  onPeriodChange,
}: {
  isLoading: boolean;
  isError: boolean;
  data: AdminQuestionsResponse | undefined;
  onRetry: () => void;
  surveyType: PulseSurveyType;
  onSurveyTypeChange: (v: PulseSurveyType) => void;
  teamId: string;
  onTeamIdChange: (v: string) => void;
  period: string;
  onPeriodChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const saveTemplate = useSaveQuestionTemplate();
  const saveOverlay = useSaveQuestionOverlay();
  const addBank = useAddBankQuestion();
  const removeBank = useRemoveBankQuestion();

  const coupleSelected = Boolean(teamId && period);

  // Copies de travail + instantane serveur adopte (JSON) + drapeau d'adoption
  // forcee (contexte change / bloc sauvegarde) — voir doctrine en entete.
  // L'adoption se fait PENDANT LE RENDU, gardee par comparaison (pattern React
  // officiel « adjusting state when props change » — react-hooks interdit le
  // setState dans un effet, et ce pattern re-rend une seule fois sans boucle).
  const [template, setTemplate] = useState<WorkingQuestion[]>([]);
  const [tplSnapshot, setTplSnapshot] = useState('[]');
  const [tplAdopt, setTplAdopt] = useState(true);
  const [overlay, setOverlay] = useState<WorkingQuestion[]>([]);
  const [ovSnapshot, setOvSnapshot] = useState('[]');
  const [ovAdopt, setOvAdopt] = useState(true);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [deleteBankFor, setDeleteBankFor] = useState<AdminBankQuestion | null>(null);

  // Changement de contexte => la prochaine data ECRASE les copies (equivalent
  // SPA du reload PoC — un brouillon d'un autre contexte ne survit pas).
  const [prevSurvey, setPrevSurvey] = useState(surveyType);
  if (surveyType !== prevSurvey) {
    setPrevSurvey(surveyType);
    setTplAdopt(true);
    setOvAdopt(true);
    setErrorCode(null);
  }
  const overlayContext = `${teamId}|${period}`;
  const [prevOverlayContext, setPrevOverlayContext] = useState(overlayContext);
  if (overlayContext !== prevOverlayContext) {
    setPrevOverlayContext(overlayContext);
    setOvAdopt(true);
  }

  if (data) {
    const nextTpl = data.template.map(toWorking);
    const nextTplJson = JSON.stringify(nextTpl);
    if (nextTplJson !== tplSnapshot || tplAdopt) {
      // La copie n'adopte que si propre (=== instantane) ou adoption forcee —
      // une invalidation d'un autre bloc n'ecrase jamais un brouillon.
      if (tplAdopt || JSON.stringify(template) === tplSnapshot) setTemplate(nextTpl);
      setTplSnapshot(nextTplJson);
      if (tplAdopt) setTplAdopt(false);
    }
    const nextOv = (data.overlay?.questions ?? []).map(toWorking);
    const nextOvJson = JSON.stringify(nextOv);
    if (nextOvJson !== ovSnapshot || ovAdopt) {
      if (ovAdopt || JSON.stringify(overlay) === ovSnapshot) setOverlay(nextOv);
      setOvSnapshot(nextOvJson);
      if (ovAdopt) setOvAdopt(false);
    }
  }

  const tplDirty = JSON.stringify(template) !== tplSnapshot;
  const ovDirty = JSON.stringify(overlay) !== ovSnapshot;
  const tplValid = template.every((q) => q.prompt.trim().length > 0);
  const ovValid = overlay.every((q) => q.prompt.trim().length > 0);

  // Apercu : modele + surcouche resolus (comme le formulaire reel les verra).
  const previewQuestions = useMemo(
    () => toPreview([...template, ...(coupleSelected ? overlay : [])]),
    [template, overlay, coupleSelected],
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (isError || !data) {
    return <QueryError message={t('gest:admin.questions.loadError')} onRetry={onRetry} />;
  }

  const pending =
    saveTemplate.isPending || saveOverlay.isPending || addBank.isPending || removeBank.isPending;

  const onMutationError = (err: unknown) => setErrorCode(adminQuestionsErrorCode(err));

  function doSaveTemplate() {
    setErrorCode(null);
    saveTemplate.mutate(
      { surveyType, questions: template.map(toPayload) },
      { onSuccess: () => setTplAdopt(true), onError: onMutationError },
    );
  }

  function doSaveOverlay() {
    if (!coupleSelected) return;
    setErrorCode(null);
    saveOverlay.mutate(
      { surveyType, teamId, period, prompts: overlay.map(toPayload) },
      { onSuccess: () => setOvAdopt(true), onError: onMutationError },
    );
  }

  function doAddBank(input: NewQuestionInput) {
    setErrorCode(null);
    addBank.mutate(
      {
        surveyType,
        questionType: input.questionType,
        prompt: input.prompt.trim(),
        label: input.label.trim(),
        low: input.low.trim(),
        high: input.high.trim(),
        category: input.category.trim(),
      },
      { onError: onMutationError },
    );
  }

  function doRemoveBank(id: string) {
    setErrorCode(null);
    removeBank.mutate({ id }, {
      onSuccess: () => setDeleteBankFor(null),
      onError: onMutationError,
    });
  }

  function confirmRemoveBank() {
    if (!deleteBankFor) return;
    doRemoveBank(deleteBankFor.id);
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title={t('gest:admin.questions.title')}
        description={t('gest:admin.questions.description')}
      >
        <GsSegmented
          value={surveyType}
          onChange={(v) => onSurveyTypeChange(v as PulseSurveyType)}
          ariaLabel={t('gest:admin.questions.survey.label')}
          options={PULSE_SURVEY_TYPES.map((s) => ({
            value: s,
            label: t(`gest:admin.questions.surveyTypes.${s}`),
          }))}
        />
      </SectionHeader>

      {errorCode ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">
          {t(`gest:admin.questions.errors.${errorCode}`)}
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Colonne edition */}
        <div className="flex flex-col gap-8">
          {/* ── Bloc MODELE ─────────────────────────────────────────────── */}
          <section className="flex flex-col gap-4">
            <SectionHeader
              title={t('gest:admin.questions.template.title')}
              description={t('gest:admin.questions.template.description')}
            >
              <SaveButton
                dirty={tplDirty}
                disabled={!tplValid}
                pending={saveTemplate.isPending}
                onSave={doSaveTemplate}
              />
            </SectionHeader>
            <div className="flex flex-col gap-3">
              {template.map((q, i) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  locked={q.coreKey != null}
                  index={i}
                  total={template.length}
                  onPatch={(patch) =>
                    setTemplate((cur) => cur.map((x) => (x.id === q.id ? { ...x, ...patch } : x)))
                  }
                  onMove={(dir) => setTemplate((cur) => reorder(cur, i, dir))}
                  onRemove={() => setTemplate((cur) => cur.filter((x) => x.id !== q.id))}
                />
              ))}
            </div>
            <AddQuestionForm
              placeholder={
                surveyType === 'member_pulse'
                  ? t('gest:admin.questions.template.addPlaceholderMember')
                  : t('gest:admin.questions.template.addPlaceholderTeam')
              }
              onAdd={(input) =>
                setTemplate((cur) => [
                  ...cur,
                  {
                    id: crypto.randomUUID(),
                    coreKey: null,
                    questionType: input.questionType,
                    prompt: input.prompt.trim(),
                    label: input.label.trim(),
                    low: input.low.trim(),
                    high: input.high.trim(),
                    required: false,
                  },
                ])
              }
            />
          </section>

          {/* ── Bloc SURCOUCHE ──────────────────────────────────────────── */}
          <section className="flex flex-col gap-4">
            <SectionHeader
              title={t('gest:admin.questions.overlay.title')}
              description={t('gest:admin.questions.overlay.description')}
            >
              {coupleSelected ? (
                <SaveButton
                  dirty={ovDirty}
                  disabled={!ovValid}
                  pending={saveOverlay.isPending}
                  onSave={doSaveOverlay}
                />
              ) : null}
            </SectionHeader>

            {/* Selecteurs equipe/periode — etat React leve (P-ADM-14). */}
            <div className={`${GS_COMPONENTS.card} flex flex-wrap items-end gap-3 p-4`}>
              <div className="flex min-w-52 flex-col gap-1.5">
                <label htmlFor="questions-team" className={labelCls}>
                  {t('gest:admin.questions.overlay.team')}
                </label>
                <GsSelect
                  value={teamId}
                  onChange={onTeamIdChange}
                  ariaLabel={t('gest:admin.questions.overlay.team')}
                  placeholder={t('gest:admin.questions.overlay.teamPlaceholder')}
                  options={data.teams.map((team) => ({ value: team.id, label: team.name }))}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="questions-period" className={labelCls}>
                  {t('gest:admin.questions.overlay.period')}
                </label>
                <input
                  id="questions-period"
                  type="month"
                  value={period}
                  onChange={(e) => onPeriodChange(e.target.value)}
                  className={GS_COMPONENTS.input}
                />
              </div>
            </div>

            {coupleSelected ? (
              <>
                <p className="text-chip text-slate-400 dark:text-slate-500">
                  {t('gest:admin.questions.overlay.count', { n: overlay.length })}
                </p>
                <div className="flex flex-col gap-3">
                  {overlay.map((q, i) => (
                    <QuestionCard
                      key={q.id}
                      question={q}
                      locked={false}
                      index={i}
                      total={overlay.length}
                      onPatch={(patch) =>
                        setOverlay((cur) => cur.map((x) => (x.id === q.id ? { ...x, ...patch } : x)))
                      }
                      onMove={(dir) => setOverlay((cur) => reorder(cur, i, dir))}
                      onRemove={() => setOverlay((cur) => cur.filter((x) => x.id !== q.id))}
                    />
                  ))}
                </div>
                <AddQuestionForm
                  placeholder={t('gest:admin.questions.overlay.addPlaceholder')}
                  onAdd={(input) =>
                    setOverlay((cur) => [
                      ...cur,
                      {
                        id: crypto.randomUUID(),
                        coreKey: null,
                        questionType: input.questionType,
                        prompt: input.prompt.trim(),
                        label: input.label.trim(),
                        low: input.low.trim(),
                        high: input.high.trim(),
                        required: false,
                      },
                    ])
                  }
                />
              </>
            ) : (
              <EmptyState
                icon={<ClipboardList size={48} aria-hidden="true" />}
                title={t('gest:admin.questions.overlay.empty')}
                description={t('gest:admin.questions.overlay.emptyHint')}
              />
            )}
          </section>

          {/* ── Bloc BANQUE ─────────────────────────────────────────────── */}
          <section className="flex flex-col gap-4">
            <SectionHeader
              title={t('gest:admin.questions.bank.title')}
              description={t('gest:admin.questions.bank.description')}
            >
              <Sparkles size={16} aria-hidden="true" className="text-slate-400 dark:text-slate-500" />
            </SectionHeader>

            {data.bank.length === 0 ? (
              // P-ADM-15 : banque vide => EmptyState — le serveur ne seme JAMAIS
              // a la lecture (le ensureBankSeeded du PoC n'est pas porte).
              <EmptyState
                icon={<Sparkles size={48} aria-hidden="true" />}
                title={t('gest:admin.questions.bank.empty')}
                description={t('gest:admin.questions.bank.emptyHint')}
              />
            ) : (
              <div className="flex flex-col gap-2">
                {data.bank.map((q) => (
                  <div
                    key={q.id}
                    className={`${GS_COMPONENTS.card} flex flex-row flex-wrap items-center justify-between gap-3 p-3`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-field text-slate-800 dark:text-slate-100">{q.prompt}</p>
                      <p className="mt-0.5 flex items-center gap-2 text-chip text-slate-500 dark:text-slate-400">
                        <span className="rounded-full border border-slate-200 px-2 py-0.5 dark:border-slate-600">
                          {t(`gest:admin.questions.types.${q.questionType}`)}
                        </span>
                        {q.category ? <span>{q.category}</span> : null}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setTemplate((cur) => [...cur, bankToWorking(q)])}
                        className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}
                      >
                        {t('gest:admin.questions.bank.insertTemplate')}
                      </button>
                      {coupleSelected ? (
                        <button
                          type="button"
                          onClick={() => setOverlay((cur) => [...cur, bankToWorking(q)])}
                          className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}
                        >
                          {t('gest:admin.questions.bank.insertOverlay')}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        aria-label={t('gest:admin.questions.bank.remove')}
                        disabled={pending}
                        onClick={() => {
                          setErrorCode(null);
                          setDeleteBankFor(q);
                        }}
                        className={dangerBtnCls}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <AddBankForm pending={addBank.isPending} onAdd={doAddBank} />
          </section>
        </div>

        {/* Colonne apercu (sticky sur desktop) */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <SurveyPreview
            surveyType={surveyType}
            periodLabel={formatPeriodLong(period || currentMonth())}
            questions={previewQuestions}
          />
        </div>
      </div>

      <ConfirmDialog
        open={deleteBankFor != null}
        variant="danger"
        title={t('gest:admin.questions.bank.delete.title')}
        message={t('gest:admin.questions.bank.delete.message', {
          prompt: deleteBankFor?.prompt ?? '',
        })}
        confirmLabel={t('gest:admin.questions.bank.delete.confirm')}
        cancelLabel={t('gest:admin.questions.bank.delete.cancel')}
        isLoading={removeBank.isPending}
        onConfirm={confirmRemoveBank}
        onCancel={() => setDeleteBankFor(null)}
      />
    </div>
  );
}

// ─── Carte d'edition d'une question (modele ou surcouche) ────────────────────

function QuestionCard({
  question,
  locked,
  index,
  total,
  onPatch,
  onMove,
  onRemove,
}: {
  question: WorkingQuestion;
  /** Question coeur : type/obligation figes, non supprimable (badge Coeur). */
  locked: boolean;
  index: number;
  total: number;
  onPatch: (patch: Partial<WorkingQuestion>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const isScale = question.questionType === 'scale_1_5';
  return (
    <div className={`${GS_COMPONENTS.card} flex flex-col gap-3 p-4`}>
      <div className="flex items-center justify-between gap-2">
        {locked ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-chip font-medium text-brand-600 dark:bg-brand-950 dark:text-brand-400">
            <Lock size={12} aria-hidden="true" />
            {t('gest:admin.questions.card.coreBadge')}
          </span>
        ) : (
          <span className="rounded-full border border-slate-200 px-2 py-0.5 text-chip text-slate-500 dark:border-slate-600 dark:text-slate-400">
            {t(`gest:admin.questions.types.${question.questionType}`)}
          </span>
        )}
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('gest:admin.questions.card.moveUp')}
            disabled={index === 0}
            onClick={() => onMove(-1)}
            className={iconBtnCls}
          >
            <ChevronUp size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={t('gest:admin.questions.card.moveDown')}
            disabled={index === total - 1}
            onClick={() => onMove(1)}
            className={iconBtnCls}
          >
            <ChevronDown size={16} aria-hidden="true" />
          </button>
          {!locked ? (
            <button
              type="button"
              aria-label={t('gest:admin.questions.card.remove')}
              onClick={onRemove}
              className={dangerBtnCls}
            >
              <Trash2 size={16} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className={labelCls}>{t('gest:admin.questions.fields.prompt')}</label>
        <textarea
          rows={2}
          maxLength={500}
          value={question.prompt}
          aria-label={t('gest:admin.questions.fields.prompt')}
          onChange={(e) => onPatch({ prompt: e.target.value })}
          className={GS_COMPONENTS.input}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {!locked ? (
          <div className="flex w-44 flex-col gap-1.5">
            <label className={labelCls}>{t('gest:admin.questions.fields.type')}</label>
            <GsSelect
              value={question.questionType}
              onChange={(v) => onPatch({ questionType: v as PulseAnswerType })}
              ariaLabel={t('gest:admin.questions.fields.type')}
              options={PULSE_ANSWER_TYPES.map((type) => ({
                value: type,
                label: t(`gest:admin.questions.types.${type}`),
              }))}
            />
          </div>
        ) : null}
        <div className="flex w-40 flex-col gap-1.5">
          <label className={labelCls}>{t('gest:admin.questions.fields.shortLabel')}</label>
          <input
            maxLength={80}
            value={question.label}
            aria-label={t('gest:admin.questions.fields.shortLabel')}
            onChange={(e) => onPatch({ label: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </div>
        {isScale ? (
          <>
            <div className="flex w-36 flex-col gap-1.5">
              <label className={labelCls}>{t('gest:admin.questions.fields.low')}</label>
              <input
                maxLength={40}
                value={question.low}
                aria-label={t('gest:admin.questions.fields.low')}
                onChange={(e) => onPatch({ low: e.target.value })}
                className={GS_COMPONENTS.input}
              />
            </div>
            <div className="flex w-36 flex-col gap-1.5">
              <label className={labelCls}>{t('gest:admin.questions.fields.high')}</label>
              <input
                maxLength={40}
                value={question.high}
                aria-label={t('gest:admin.questions.fields.high')}
                onChange={(e) => onPatch({ high: e.target.value })}
                className={GS_COMPONENTS.input}
              />
            </div>
          </>
        ) : null}
        {!locked ? (
          <div className="flex w-36 flex-col gap-1.5">
            <label className={labelCls}>{t('gest:admin.questions.fields.answer')}</label>
            <GsSelect
              value={question.required ? 'true' : 'false'}
              onChange={(v) => onPatch({ required: v === 'true' })}
              ariaLabel={t('gest:admin.questions.fields.answer')}
              options={[
                { value: 'false', label: t('gest:admin.questions.fields.optional') },
                { value: 'true', label: t('gest:admin.questions.fields.required') },
              ]}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── Formulaires d'ajout (question / suggestion) ─────────────────────────────

interface NewQuestionInput {
  prompt: string;
  questionType: PulseAnswerType;
  label: string;
  low: string;
  high: string;
  category: string;
}

const EMPTY_INPUT: NewQuestionInput = {
  prompt: '', questionType: 'scale_1_5', label: '', low: '', high: '', category: '',
};

function QuestionInputFields({
  input,
  set,
  withCategory,
}: {
  input: NewQuestionInput;
  set: (patch: Partial<NewQuestionInput>) => void;
  withCategory: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex w-44 flex-col gap-1.5">
        <label className={labelCls}>{t('gest:admin.questions.fields.type')}</label>
        <GsSelect
          value={input.questionType}
          onChange={(v) => set({ questionType: v as PulseAnswerType })}
          ariaLabel={t('gest:admin.questions.fields.type')}
          options={PULSE_ANSWER_TYPES.map((type) => ({
            value: type,
            label: t(`gest:admin.questions.types.${type}`),
          }))}
        />
      </div>
      <div className="flex w-40 flex-col gap-1.5">
        <label className={labelCls}>{t('gest:admin.questions.fields.shortLabel')}</label>
        <input
          maxLength={80}
          value={input.label}
          aria-label={t('gest:admin.questions.fields.shortLabel')}
          onChange={(e) => set({ label: e.target.value })}
          className={GS_COMPONENTS.input}
        />
      </div>
      {input.questionType === 'scale_1_5' ? (
        <>
          <div className="flex w-36 flex-col gap-1.5">
            <label className={labelCls}>{t('gest:admin.questions.fields.low')}</label>
            <input
              maxLength={40}
              value={input.low}
              aria-label={t('gest:admin.questions.fields.low')}
              onChange={(e) => set({ low: e.target.value })}
              className={GS_COMPONENTS.input}
            />
          </div>
          <div className="flex w-36 flex-col gap-1.5">
            <label className={labelCls}>{t('gest:admin.questions.fields.high')}</label>
            <input
              maxLength={40}
              value={input.high}
              aria-label={t('gest:admin.questions.fields.high')}
              onChange={(e) => set({ high: e.target.value })}
              className={GS_COMPONENTS.input}
            />
          </div>
        </>
      ) : null}
      {withCategory ? (
        <div className="flex w-36 flex-col gap-1.5">
          <label className={labelCls}>{t('gest:admin.questions.fields.category')}</label>
          <input
            maxLength={60}
            value={input.category}
            aria-label={t('gest:admin.questions.fields.category')}
            onChange={(e) => set({ category: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </div>
      ) : null}
    </div>
  );
}

function AddQuestionForm({
  placeholder,
  onAdd,
}: {
  placeholder: string;
  onAdd: (input: NewQuestionInput) => void;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState<NewQuestionInput>(EMPTY_INPUT);
  const set = (patch: Partial<NewQuestionInput>) => setInput((cur) => ({ ...cur, ...patch }));

  function add() {
    if (!input.prompt.trim()) return;
    onAdd(input);
    setInput(EMPTY_INPUT);
  }

  return (
    <div className={`${GS_COMPONENTS.card} flex flex-col gap-3 border-dashed p-4`}>
      <p className={`${labelCls} inline-flex items-center gap-2`}>
        <MessageSquarePlus size={14} aria-hidden="true" />
        {t('gest:admin.questions.add.title')}
      </p>
      <textarea
        rows={2}
        maxLength={500}
        value={input.prompt}
        placeholder={placeholder}
        aria-label={t('gest:admin.questions.fields.prompt')}
        onChange={(e) => set({ prompt: e.target.value })}
        className={GS_COMPONENTS.input}
      />
      <div className="flex flex-wrap items-end gap-3">
        <QuestionInputFields input={input} set={set} withCategory={false} />
        <button
          type="button"
          onClick={add}
          disabled={!input.prompt.trim()}
          className={`${GS_COMPONENTS.btnAction} ml-auto inline-flex touch-manipulation items-center gap-1.5`}
        >
          <Plus size={14} aria-hidden="true" />
          {t('gest:admin.questions.add.submit')}
        </button>
      </div>
    </div>
  );
}

function AddBankForm({
  pending,
  onAdd,
}: {
  pending: boolean;
  onAdd: (input: NewQuestionInput) => void;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState<NewQuestionInput>(EMPTY_INPUT);
  const set = (patch: Partial<NewQuestionInput>) => setInput((cur) => ({ ...cur, ...patch }));

  function add() {
    if (!input.prompt.trim()) return;
    onAdd(input);
    setInput(EMPTY_INPUT);
  }

  return (
    <div className={`${GS_COMPONENTS.card} flex flex-col gap-3 border-dashed p-4`}>
      <p className={`${labelCls} inline-flex items-center gap-2`}>
        <MessageSquarePlus size={14} aria-hidden="true" />
        {t('gest:admin.questions.bank.addTitle')}
      </p>
      <textarea
        rows={2}
        maxLength={500}
        value={input.prompt}
        placeholder={t('gest:admin.questions.bank.addPlaceholder')}
        aria-label={t('gest:admin.questions.fields.prompt')}
        onChange={(e) => set({ prompt: e.target.value })}
        className={GS_COMPONENTS.input}
      />
      <div className="flex flex-wrap items-end gap-3">
        <QuestionInputFields input={input} set={set} withCategory />
        <button
          type="button"
          onClick={add}
          disabled={pending || !input.prompt.trim()}
          className={`${GS_COMPONENTS.btnAction} ml-auto inline-flex touch-manipulation items-center gap-1.5`}
        >
          <Plus size={14} aria-hidden="true" />
          {t('gest:admin.questions.add.submit')}
        </button>
      </div>
    </div>
  );
}

// ─── Bouton d'enregistrement partage ─────────────────────────────────────────

function SaveButton({
  dirty,
  disabled,
  pending,
  onSave,
}: {
  dirty: boolean;
  disabled: boolean;
  pending: boolean;
  onSave: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      disabled={!dirty || disabled || pending}
      onClick={onSave}
      className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-1.5`}
    >
      {pending ? (
        <Loader2 size={14} aria-hidden="true" className="animate-spin" />
      ) : (
        <Save size={14} aria-hidden="true" />
      )}
      {dirty ? t('gest:admin.questions.actions.save') : t('gest:admin.questions.actions.saved')}
    </button>
  );
}
